import ContentContainer from '@/features/shared/ContentContainer';
import DetailContent from '@/features/shared/DetailContent';
import { MuiContainer, MuiTab, MuiTabs, MuiTypography } from '@/features/shared/material-ui';
import RichText from '@/features/shared/RichText';
import { colorMap, SpacingSizes } from '@/utils/constants';
import { resolveTheme } from '@/utils/resolvers';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, {
  forwardRef,
  memo,
  PropsWithChildren,
  RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { HorizontalTopContentProps } from './HorizontalTopContent.interface';
import useStyles from './HorizontalTopContent.styles';
import { useScrollNavigation } from './useScrollNavigation';

const a11yProps = (index: any) => {
  return {
    id: `horizontal-top-tab-${index}`,
    'aria-controls': `horizontal-top-tabpanel-${index}`,
  };
};

enum Selectors {
  TabContainer = '.custom__complex-tab--horizontal-top',
  TabContentHidden = '[role="tabpanel"][hidden]',
}

interface TabPanelProps {
  children?: any;
  index?: any;
  value?: any;
  panelName?: string;
  classes?: any;
}

const mapTabColorToContrastColor = (tabColor = '') => {
  if (RegExp(colorMap.white).test(tabColor)) {
    return colorMap.eggshell;
  }

  if (RegExp(colorMap.eggshell).test(tabColor)) {
    return colorMap.white;
  }

  if (RegExp(colorMap.charcoal).test(tabColor)) {
    return colorMap.black;
  }

  if (RegExp(colorMap['medium-gray']).test(tabColor)) {
    return colorMap['dark-gray'];
  }

  if (RegExp(colorMap['dark-gray']).test(tabColor)) {
    return colorMap['medium-gray'];
  }

  if (RegExp(colorMap.black).test(tabColor)) {
    return colorMap.charcoal;
  }

  return '';
};

const mapVariantToDetailContentClassName =
  (classes: any) =>
  ({ variant }: any) => {
    if (/^rtc$/.test(variant)) {
      return classes.rtc;
    }

    return '';
  };

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, panelName, classes, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`horizontal-${panelName}-${index}`}
      aria-labelledby={`horizontal-top-tab-${index}`}
      className={clsx(classes.tabPanelContent, classes.tabPanelAnimated)}
      {...other}
    >
      {children}
    </div>
  );
};

const DetailContentPanels = ({
  classes,
  tabs = [],
  value = 0,
  theme,
  children,
  tabColor,
}: PropsWithChildren<{
  classes: any;
  tabs: TabContentType[];
  value: number;
  tabColor?: string;
  theme?: any;
}>) => {
  const rootClasses = clsx(
    classes.tabPanel,
    'custom__complex-tab--horizontal-top',
    `bg-color-${tabColor}`,
    'gntc-horizontal-top-content',
  );
  return (
    <div className={rootClasses}>
      {children}
      {tabs.map(({ tabDetailContent = {} }, i) => {
        const { detailContent = [], heading, superHeading } = tabDetailContent;

        return (
          <TabPanel
            // eslint-disable-next-line react/no-array-index-key
            key={`tabPanel_${i}`}
            value={value}
            panelName="tabpanel"
            index={i}
            classes={classes}
          >
            {superHeading && (
              <MuiTypography variant="overline" component="h5" className={classes.superheading}>
                {superHeading}
              </MuiTypography>
            )}
            {heading && (
              <RichText
                customStyles
                theme={theme}
                className={clsx(classes.headingRTC, 'gtm-headline')}
                content={heading}
              />
            )}
            <DetailContent
              content={detailContent}
              theme={theme}
              mapVariantToClassName={mapVariantToDetailContentClassName(classes)}
            />
          </TabPanel>
        );
      })}
    </div>
  );
};

const CustomTabComponent = forwardRef((props: any, ref: any) => {
  const { children, title, tabIndex, ...tabProps } = props;
  const classes = useStyles({});
  const { text, logo } = title;
  const hasLogo = logo && logo.original;
  const rootClasses = clsx('heading', classes.heading);

  return (
    <div {...tabProps} ref={ref}>
      {!hasLogo && text && (
        <MuiTypography className={rootClasses} variant="body1" component="h3">
          {text}
        </MuiTypography>
      )}
      {hasLogo && (
        <img
          src={logo.original?.url}
          alt={logo.altText}
          onLoad={() => {
            if (tabIndex === 0) {
              window.dispatchEvent(new CustomEvent('resize'));
            }
          }}
        />
      )}
    </div>
  );
});

const setTabContainerHeight = ({
  ref,
  isMobile,
  tabContainerHeight,
}: {
  ref: RefObject<HTMLElement>;
  isMobile: boolean;
  tabContainerHeight: number;
}) => {
  if (!ref?.current) {
    return setTimeout(() => {});
  }

  const tabContainerEl = ref?.current?.querySelector(Selectors.TabContainer) as HTMLElement;

  if (isMobile || !tabContainerHeight) {
    return setTimeout(() => {
      tabContainerEl.style.height = '';
    });
  }

  return setTimeout(() => {
    tabContainerEl.style.height = `${tabContainerHeight}px`;
  });
};

const HorizontalTopContent = ({
  tabs = [],
  containerProps,
  tabColor,
  contentButtons,
}: Br.PropsWithContentButtons<HorizontalTopContentProps>) => {
  const classes = useStyles({});
  const [value, setValue] = useState(0);
  const [backgroundImages, setBackgroundImages] = useState<any[]>([]);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const tabContainerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(0);

  const { containerRef, contentRef, hasScrollEffect, setTabPosition } = useScrollNavigation({
    tabsLength: tabs.length,
    setTabIndex: setValue,
  });

  const hasBackgroundImage = useMemo(
    () =>
      (containerProps?.backgroundImage ||
        (backgroundImages.length > 0 && backgroundImages.some((img) => img !== undefined))) &&
      !isMobile,
    [backgroundImages, containerProps?.backgroundImage, isMobile],
  );

  const handleChange = React.useCallback(
    (_, newValue: number) => {
      if (hasScrollEffect) {
        setTabPosition(newValue);
        return;
      }
      setValue(newValue);
    },
    [hasScrollEffect, setTabPosition],
  );

  useLayoutEffect(() => {
    let timeout: any;

    const updateContainerHeight = () => {
      if (!tabContainerRef?.current) {
        return;
      }

      setContainerHeight(0);
      const tabContainerEl = tabContainerRef?.current?.querySelector(
        Selectors.TabContainer,
      ) as HTMLElement;
      const tabsHiddenEl = tabContainerRef?.current?.querySelectorAll(Selectors.TabContentHidden);
      tabsHiddenEl.forEach((item) => item.removeAttribute('hidden'));

      timeout = setTimeout(() => {
        const tabContainerHeight = tabContainerEl.offsetHeight;
        setContainerHeight(tabContainerHeight);
        tabsHiddenEl.forEach((item) => item.setAttribute('hidden', ''));
      });
    };

    window.addEventListener('resize', updateContainerHeight, { passive: true });

    updateContainerHeight();

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', updateContainerHeight);
    };
  }, []);

  useEffect(() => {
    if (!tabs.length) return;

    const bgs = tabs.map((tab) => tab?.tabContainerBackgroundImage || undefined);

    setBackgroundImages(bgs);
  }, [containerProps, tabs]);

  useLayoutEffect(() => {
    const timeout = setTabContainerHeight({
      ref: tabContainerRef,
      isMobile,
      tabContainerHeight: containerHeight,
    });

    return () => {
      clearTimeout(timeout);
    };
  }, [tabContainerRef, isMobile, containerHeight]);
  const tabTheme = resolveTheme('bg-color')({ rawVal: tabColor });
  const rootClasses = clsx(classes.root, `${tabTheme}-theme`);
  const rootTabsClasses = clsx(
    'gntc-horizontal-top-content-tabs',
    classes.tabs,
    `bg-color-${tabColor}`,
  );
  const rootTabClasses = clsx(classes.tab, 'gtm-tab');

  return (
    <div
      data-testid="horizontal-top-content__container"
      ref={containerRef}
      className={classes.container}
    >
      <ContentContainer
        {...containerProps}
        {...(hasScrollEffect && {
          topSpacing: SpacingSizes.none,
          bottomSpacing: SpacingSizes.none,
          isBackgroundSticky: true,
        })}
        topBackgroundColor={hasBackgroundImage ? 'transparent' : containerProps?.topBackgroundColor}
        bottomBackgroundColor={
          hasBackgroundImage ? 'transparent' : containerProps?.bottomBackgroundColor
        }
        mobileTopBackgroundColor={containerProps?.mobileTopBackgroundColor}
        mobileBottomBackgroundColor={containerProps?.mobileBottomBackgroundColor}
        multipleBackground
        multipleBackgroundImage={backgroundImages}
        multipleBackgroundSelected={value}
        mobileTopSpacing={SpacingSizes.small}
        data-component-variant="horizontal-top-content"
      >
        <MuiContainer className="large">
          <div className={rootClasses} ref={contentRef}>
            <div className={classes.content}>
              {tabs.length > 0 && (
                <MuiTabs
                  orientation="horizontal"
                  variant="scrollable"
                  scrollButtons={isMobile ? 'on' : 'off'}
                  value={value}
                  onChange={handleChange}
                  aria-label="Horizontal Top MuiTabs"
                  className={rootTabsClasses}
                >
                  {tabs.map(({ tabTitle }, i) => (
                    <MuiTab
                      // eslint-disable-next-line react/no-array-index-key
                      key={`tab_${i}`}
                      title={tabTitle}
                      {...a11yProps(i)}
                      component={CustomTabComponent}
                      className={rootTabClasses}
                    />
                  ))}
                </MuiTabs>
              )}

              <div ref={tabContainerRef}>
                <DetailContentPanels
                  tabColor={tabColor}
                  theme={tabTheme}
                  tabs={tabs}
                  value={value}
                  classes={classes}
                >
                  {contentButtons?.content}
                </DetailContentPanels>
              </div>
            </div>
          </div>
          {hasScrollEffect && !!tabs.length && (
            <div>
              {Array.from({ length: tabs.length }).map((_, key) => (
                <div className={classes.ghostBlock} key={key.toString()}>
                  <span aria-hidden="true">&nbsp;</span>
                </div>
              ))}
            </div>
          )}
        </MuiContainer>
      </ContentContainer>
    </div>
  );
};

export default memo(HorizontalTopContent);
